import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { GitBranch, Loader2, XCircle } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/datetime';
import { JobFailedAlert } from './JobFailedAlert';
import { ResubmitDialog } from './ResubmitDialog';
import { jobStatusLabel, finalDecisionLabel } from '@/lib/enumLabels';
import type { Job, JobStatus } from '@/types';

interface JobStatusHeaderProps {
  job: Job;
  onCancelled?: () => void;
  /** Called after a successful "Retry pipeline" hit so the parent page can
   *  re-fetch job state. Same shape as onCancelled — just fires after the
   *  recovery endpoint returns. */
  onRetried?: () => void;
}

const STATUS_BADGE: Record<JobStatus, { className: string }> = {
  pending: { className: 'bg-muted text-muted-foreground border-border' },
  processing: { className: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 animate-pulse' },
  completed: { className: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800' },
  failed: { className: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800' },
  cancelled: { className: 'bg-muted text-muted-foreground border-border' },
};

/**
 * Determines when "Create Revised Version" is shown.
 *
 * FR-078: resubmit is available when:
 *   - job is COMPLETED (reviewed, any final_decision) or FAILED
 *   - AND no child revision has already been started (childJobId guard in parent)
 *   - AND user is admin or reviewer
 */
function canResubmit(job: Job): boolean {
  return job.final_decision !== null || job.status === 'failed' || job.status === 'cancelled';
}

export function JobStatusHeader({ job, onCancelled, onRetried }: JobStatusHeaderProps) {
  const { canReview } = useAuth();
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [resubmitOpen, setResubmitOpen] = useState(false);

  const statusConfig = STATUS_BADGE[job.status];
  const canCancel =
    canReview && (job.status === 'pending' || job.status === 'processing');
  const showResubmit = canReview && canResubmit(job);
  const canRetry = canReview && job.status === 'failed';

  async function handleCancel() {
    setIsCancelling(true);
    setCancelError(null);
    try {
      await api.post(`/api/v1/jobs/${job.id}/cancel`, {});
      onCancelled?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setCancelError('This job has already completed and cannot be cancelled.');
        onCancelled?.();
      } else {
        setCancelError('Failed to cancel. Please try again.');
      }
      setIsCancelling(false);
    }
  }

  return (
    <>
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground truncate" title={job.file_name}>
              {job.file_name}
            </h1>
            <p className="text-xs font-mono text-muted-foreground mt-0.5">
              Uploaded {fmtDateTime(job.created_at)} · job <span title={job.id}>{job.id.slice(0, 8)}</span>
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge
                variant="outline"
                className={cn('text-xs', statusConfig.className)}
                aria-label={`Job status: ${jobStatusLabel[job.status]}`}
              >
                {jobStatusLabel[job.status]}
              </Badge>
              {(job.review_status === 'claimed' || job.review_status === 'in_progress') && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Under Review
                </Badge>
              )}
              {job.final_decision && (
                <Badge variant="outline" className="text-xs">
                  {finalDecisionLabel[job.final_decision]}
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Cancel processing */}
            {canCancel && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/5 gap-1.5">
                    <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    Cancel Processing
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel processing?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to cancel processing for this contract? This action cannot be undone. You can resubmit the contract later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep processing</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => { void handleCancel(); }}
                      disabled={isCancelling}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isCancelling && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />}
                      {isCancelling ? 'Cancelling...' : 'Cancel Processing'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {/* Create Revised Version (FR-078) */}
            {showResubmit && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => { setResubmitOpen(true); }}
                aria-haspopup="dialog"
              >
                <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
                Create Revised Version
              </Button>
            )}
          </div>
        </div>

        {cancelError && (
          <p role="alert" className="text-xs text-destructive mt-1">{cancelError}</p>
        )}

        {job.status === 'failed' && (
          <JobFailedAlert job={job} canRetry={canRetry} onRetried={onRetried} />
        )}

        {(job.status === 'pending' || job.status === 'processing') && (
          <p className="text-xs text-muted-foreground mt-2">
            You can leave this page and come back — progress is saved. The contract will appear in your review queue when processing is complete.
          </p>
        )}
      </div>

      {/* Resubmit dialog rendered outside the header flow to avoid stacking context issues */}
      <ResubmitDialog
        job={job}
        open={resubmitOpen}
        onClose={() => { setResubmitOpen(false); }}
      />
    </>
  );
}
